import { pdkAxios } from '@/lib/pdk-axios';
import { dealStore, lenderStore } from '@/models/stores/index';
import type { Lender } from '@/models/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const useLenderMutations = ({
  dealId,
  dealShortId,
  setDialogIsOpen,
  onUpdateSuccess,
  onUpdateError,
  onCreateSuccess,
  onCreateError,
}: {
  dealId: string | undefined | null;
  dealShortId: string | undefined;
  setDialogIsOpen: (isOpen: boolean) => void;
  onUpdateSuccess?: (data: null, variables: { lender: Lender }) => void;
  onUpdateError?: (error: Error, variables: { lender: Lender }) => void;
  onCreateSuccess?: (data: null, variables: { lender: Lender }) => void;
  onCreateError?: (error: Error, variables: { lender: Lender }) => void;
}) => {
  const queryClient = useQueryClient();
  const createLenderMutation = useMutation({
    onMutate: async ({ lender }) => {
      lenderStore.add(lender);
    },
    mutationFn: async ({ lender }: { lender: Lender }) => {
      const response = await pdkAxios
        .post(`/lenders/create`, {
          id: lender.id,
          name: lender.name,
          email: lender.email,
          phone: lender.phone,
          lender_type: lender.lender_type,
        })
        .catch((e) => {
          console.error('Error creating lender', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: (data, { lender }) => {
      setDialogIsOpen(false);

      // UX NOTE: we want to update the lender on the deal immediately after it is created
      // so that the user can see the lender on the deal immediately
      updateLenderMutation.mutate({
        lender: lender,
      });

      onCreateSuccess?.(data, { lender });
    },
    onError: (error, variables) => {
      onCreateError?.(error, variables);

      console.error('Failed to create lender', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['lenders'] });
    },
  });

  const updateLenderMutation = useMutation({
    onMutate: async ({ lender }) => {
      if (!dealId) {
        throw new Error('updateLenderMutation: dealId is required');
      }

      dealStore.updateLender({ id: dealId, lenderId: lender.id });
    },
    mutationFn: async ({ lender }: { lender: Lender }) => {
      const response = await pdkAxios
        .post(`/deals/${dealId}/funding/lender`, {
          lender_id: lender.id,
        })
        .catch((e) => {
          console.error('Error updating lender', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: (data, { lender }) => {
      setDialogIsOpen(false);

      onUpdateSuccess?.(data, { lender });

      // TODO: add lender to the deal
    },
    onError: (error, variables) => {
      onUpdateError?.(error, variables);

      console.error('Failed to update lender', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealShortId] });
    },
  });

  const deleteLenderMutation = useMutation({
    onMutate: async () => {
      if (!dealId) {
        throw new Error('deleteLenderMutation: dealId is required');
      }

      dealStore.removeLender({ id: dealId });
    },
    mutationFn: async ({ lender }: { lender: Lender }) => {
      const response = await pdkAxios
        .post(`/deals/${dealId}/funding/lender/${lender.id}/delete`, {
          lender_id: lender.id,
        })
        .catch((e) => {
          console.error('Error deleting lender', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: (data, { lender }) => {
      setDialogIsOpen(false);

      onUpdateSuccess?.(data, { lender });

      // TODO: add lender to the deal
    },
    onError: (error, variables) => {
      onUpdateError?.(error, variables);

      console.error('Failed to delete lender', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealShortId] });
    },
  });

  return {
    createLenderMutation,
    updateLenderMutation,
    deleteLenderMutation,
  };
};

export default useLenderMutations;
